import { getDraft, saveDraft } from "@/components/agent-session/chatDrafts";
import { safeLocalStorage } from "@/lib/storage";

/* ---------------------------------------------------------------------------
 * State shared between the new-session skeleton (SessionSkeleton.tsx) and the
 * real NewSessionView:
 *
 * - a handoff for text typed into the skeleton's live composer while the app
 *   is still loading, adopted into the real composer's draft on mount.
 * -------------------------------------------------------------------------*/

const COMPOSER_HANDOFF_KEY = "codesmith-skeleton-composer-handoff";
// Long enough to span a slow cold load; short enough that text abandoned by
// a navigation away mid-load doesn't resurface on a much later visit.
const COMPOSER_HANDOFF_TTL_MS = 5 * 60 * 1000;

function readComposerHandoff(): string {
  const raw = safeLocalStorage.get(COMPOSER_HANDOFF_KEY);
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw) as { text?: string; timestamp?: number };
    if (
      !parsed.text ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > COMPOSER_HANDOFF_TTL_MS
    ) {
      return "";
    }
    return parsed.text;
  } catch {
    return "";
  }
}

/** Persist text typed into the skeleton composer while the app loads. */
export function saveSkeletonComposerText(text: string) {
  if (!text) {
    safeLocalStorage.remove(COMPOSER_HANDOFF_KEY);
    return;
  }
  safeLocalStorage.set(
    COMPOSER_HANDOFF_KEY,
    JSON.stringify({ text, timestamp: Date.now() }),
  );
}

/**
 * Current handoff text. The loading sequence stacks several skeleton
 * fallbacks (auth check, dynamic chunk, page suspense); each freshly mounted
 * skeleton composer seeds from this so remounts don't drop keystrokes.
 */
export function peekSkeletonComposerText(): string {
  return readComposerHandoff();
}

/**
 * Consume the handoff into the real composer's draft. Called by
 * NewSessionView during its first render, before AgentChatInput mounts and
 * reads the draft. A draft that already has text (a reopened `?draft=`
 * entity) wins over the handoff.
 */
export function adoptSkeletonComposerText(
  composerSessionId: string,
  installationModelId?: number | null,
) {
  const text = readComposerHandoff();
  safeLocalStorage.remove(COMPOSER_HANDOFF_KEY);
  if (!text || getDraft(composerSessionId)) return;
  saveDraft(composerSessionId, text, undefined, installationModelId);
}
